import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Star, Trash2, Plus, Camera, Film, User as UserIcon, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import PhotoPicker from "@/components/PhotoPicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const KIND_META = {
  body:  { icon: UserIcon, label: "Body shot" },
  face:  { icon: Camera,   label: "Face shot" },
  video: { icon: Film,     label: "Video" },
  other: { icon: ImageIcon, label: "Reference" },
};

/**
 * IdentityMediaManager — view / add / set primary / delete
 * user's identity photos & videos (used by the AI Try-On model).
 */
export default function IdentityMediaManager() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pendingKind, setPendingKind] = useState("body");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/identity");
      setMedia(data.media || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSelect = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const willSetPrimary = media.length === 0;
      for (const f of files) {
        const kind = f.type?.startsWith("video/") ? "video" : pendingKind;
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        if (willSetPrimary && f === files[0]) fd.append("set_primary", "true");
        const { data } = await api.post("/users/me/identity", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMedia(data.all);
      }
      toast.success(`Added ${files.length} ${files.length === 1 ? "file" : "files"}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally { setUploading(false); }
  };

  const onVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "video");
      const { data } = await api.post("/users/me/identity", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMedia(data.all);
      toast.success("Video added");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally { setUploading(false); }
  };

  const setPrimary = async (m) => {
    if (m.is_primary) return;
    setBusyId(m.id);
    try {
      const { data } = await api.patch(`/users/me/identity/${m.id}`, { set_primary: true });
      setMedia(data.all);
      toast.success("Set as primary — Try-On will use this");
    } catch { toast.error("Could not update"); }
    finally { setBusyId(null); }
  };

  const remove = async (m) => {
    if (!confirm(`Remove this ${m.kind}?`)) return;
    setBusyId(m.id);
    try {
      const { data } = await api.delete(`/users/me/identity/${m.id}`);
      setMedia(data.all);
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
    finally { setBusyId(null); }
  };

  const photos = media.filter((m) => m.kind !== "video");
  const videos = media.filter((m) => m.kind === "video");
  const primary = media.find((m) => m.is_primary);

  return (
    <div className="border border-border p-6 lg:p-8" data-testid="identity-media-section">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Identity gallery</p>
          <h2 className="font-serif text-2xl tracking-tight mt-2">Photos & videos</h2>
          <p className="text-xs text-muted-foreground mt-2 max-w-md leading-relaxed">
            These shots are the foundation of every AI Try-On. The starred one is the default — add more for variety in lighting, angles or seasons.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PhotoPicker
            onSelect={onSelect}
            multiple
            cameraFacing="user"
            testId="identity-photo-picker"
            disabled={uploading}
          >
            <Button
              variant="outline"
              disabled={uploading}
              data-testid="identity-add-photo-btn"
              className="rounded-none uppercase tracking-[0.18em] text-[10px] h-10"
            >
              {uploading
                ? <><Loader2 size={12} className="mr-2 animate-spin" /> Uploading</>
                : <><Plus size={12} className="mr-2" /> Photo</>}
            </Button>
          </PhotoPicker>
          <label className="inline-flex">
            <input
              type="file"
              accept="video/*"
              capture="user"
              className="hidden"
              onChange={onVideoSelect}
              data-testid="identity-video-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              data-testid="identity-add-video-btn"
              onClick={(e) => e.currentTarget.previousSibling.click()}
              className="rounded-none uppercase tracking-[0.18em] text-[10px] h-10"
            >
              <Film size={12} className="mr-2" /> Video
            </Button>
          </label>
        </div>
      </div>

      {/* Kind picker for photo uploads */}
      <div className="mt-4 flex gap-2 flex-wrap">
        {["body", "face", "other"].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setPendingKind(k)}
            data-testid={`identity-kind-${k}`}
            className={`px-3 h-7 text-[10px] uppercase tracking-[0.18em] border ${
              pendingKind === k ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
            }`}
          >
            {KIND_META[k].label}
          </button>
        ))}
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground self-center ml-1">
          ← Tap a category before adding photos
        </span>
      </div>

      {loading ? (
        <div className="mt-8 flex justify-center py-10"><Loader2 className="animate-spin" /></div>
      ) : media.length === 0 ? (
        <div className="mt-8 border border-dashed border-border p-10 text-center">
          <UserIcon size={28} className="mx-auto text-muted-foreground" />
          <p className="font-serif text-2xl mt-3">No identity media yet.</p>
          <p className="text-xs text-muted-foreground mt-2">
            Add at least one full-body photo so AI Try-On can dress you.
          </p>
        </div>
      ) : (
        <>
          {photos.length > 0 && (
            <div className="mt-6">
              <p className="eyebrow mb-3">Photos · {photos.length}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                {photos.map((m) => (
                  <MediaTile
                    key={m.id}
                    m={m}
                    busy={busyId === m.id}
                    onPrimary={() => setPrimary(m)}
                    onPreview={() => setPreview(m)}
                    onRemove={() => remove(m)}
                  />
                ))}
              </div>
            </div>
          )}
          {videos.length > 0 && (
            <div className="mt-8">
              <p className="eyebrow mb-3">Videos · {videos.length}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {videos.map((m) => (
                  <VideoTile
                    key={m.id}
                    m={m}
                    busy={busyId === m.id}
                    onRemove={() => remove(m)}
                  />
                ))}
              </div>
            </div>
          )}
          {primary && (
            <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              AI Try-On will use your <span className="text-accent font-semibold">{KIND_META[primary.kind]?.label || primary.kind}</span> · {new Date(primary.created_at).toLocaleDateString()}
            </p>
          )}
        </>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="rounded-none max-w-xl" data-testid="identity-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {preview && (KIND_META[preview.kind]?.label || preview.kind)}
            </DialogTitle>
          </DialogHeader>
          {preview?.kind === "video" ? (
            <video src={fileUrl(preview.path)} controls className="w-full" />
          ) : (
            <img src={fileUrl(preview?.path)} alt="" className="w-full" />
          )}
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {preview && new Date(preview.created_at).toLocaleString()}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MediaTile({ m, busy, onPrimary, onPreview, onRemove }) {
  const KindIcon = KIND_META[m.kind]?.icon || ImageIcon;
  return (
    <div
      className={`relative group border bg-card overflow-hidden ${
        m.is_primary ? "border-accent ring-1 ring-accent/40" : "border-border hover:border-foreground"
      }`}
      data-testid={`identity-tile-${m.id}`}
    >
      <button
        type="button"
        onClick={onPreview}
        className="block aspect-square w-full bg-muted"
        aria-label="Preview"
      >
        <img src={fileUrl(m.path)} alt="" className="h-full w-full object-cover" loading="lazy" />
      </button>
      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-background/85 backdrop-blur text-[9px] uppercase tracking-[0.18em] flex items-center gap-1">
        <KindIcon size={9} /> {m.kind}
      </div>
      {m.is_primary && (
        <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-accent text-accent-foreground text-[9px] uppercase tracking-[0.18em] flex items-center gap-1">
          <Star size={9} fill="currentColor" /> Primary
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 p-1.5 flex justify-between opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/70 to-transparent">
        {!m.is_primary && (
          <button
            type="button"
            onClick={onPrimary}
            disabled={busy}
            data-testid={`identity-primary-${m.id}`}
            aria-label="Set as primary"
            className="h-7 w-7 bg-background/90 hover:bg-accent hover:text-accent-foreground inline-flex items-center justify-center"
          >
            <Star size={11} />
          </button>
        )}
        <span />
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          data-testid={`identity-remove-${m.id}`}
          aria-label="Delete"
          className="h-7 w-7 bg-background/90 hover:bg-destructive hover:text-destructive-foreground inline-flex items-center justify-center"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
        </button>
      </div>
    </div>
  );
}

function VideoTile({ m, busy, onRemove }) {
  return (
    <div
      className="relative group border border-border bg-card"
      data-testid={`identity-video-tile-${m.id}`}
    >
      <video
        src={fileUrl(m.path)}
        className="w-full aspect-video object-cover bg-black"
        controls
        playsInline
        preload="metadata"
      />
      <div className="p-2 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Video · {new Date(m.created_at).toLocaleDateString()}
        </p>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          aria-label="Delete"
          data-testid={`identity-remove-${m.id}`}
          className="p-1.5 text-muted-foreground hover:text-destructive"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
        </button>
      </div>
    </div>
  );
}
